import { Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { IconTile } from '@/components/ui/icon-tile';
import { colors, radius, spacing } from '@/theme/tokens';
import type { Policy } from '@/types/domain';
import { daysUntil, formatDate, formatPln } from '@/utils/format';

const TYPE_ICON: Record<Policy['type'], keyof typeof Ionicons.glyphMap> = {
  CAR_OC: 'car-outline',
  CAR_AC: 'car-sport-outline',
  HOME: 'home-outline',
  LIFE: 'heart-outline',
  NNW: 'body-outline',
  TRAVEL: 'airplane-outline',
  HEALTH: 'medkit-outline',
  OTHER: 'document-text-outline',
};

/**
 * Policy card — "variant A" (owner pick): minimalist. A calendar icon + the
 * od–do date range (not just "ochrona do"), a compact status pill (dot), and a
 * slim time-progress bar. Fewer, smaller icons than before.
 */
export function PolicyCard({ policy, onDetails }: { policy: Policy; onDetails?: () => void }) {
  const { t } = useTranslation();

  const todayIso = new Date().toISOString().slice(0, 10);
  const days = policy.endDate ? daysUntil(policy.endDate) : null;
  const ended = !policy.openEnded && !!policy.endDate && policy.endDate < todayIso;
  const endingSoon = !ended && days !== null && days < 30;

  // Status pill: green active / orange ending / grey ended.
  const status = ended
    ? { fg: colors.outline, bg: colors.surface, label: t('policies.statusEnded') }
    : endingSoon
      ? { fg: colors.warning, bg: colors.warningBg, label: t('policies.statusEndingSoon') }
      : { fg: colors.success, bg: colors.successBg, label: t('policies.statusActive') };

  // elapsed fraction of the protection period (only when we know both dates)
  let elapsedPct: number | null = null;
  if (policy.startDate && policy.endDate && !policy.openEnded) {
    const total = daysUntil(policy.endDate) - daysUntil(policy.startDate);
    if (total > 0) elapsedPct = Math.max(0, Math.min(100, ((total - days!) / total) * 100));
  }

  const premiumValue =
    policy.premiumPeriod === 'MONTHLY'
      ? `${formatPln(policy.premiumGr)}${t('policies.perMonthSuffix')}`
      : formatPln(policy.premiumGr);

  const dateRange =
    policy.openEnded || !policy.endDate
      ? policy.startDate
        ? `${t('policies.coverageFrom', { date: formatDate(policy.startDate) })} · ${t('policies.openEnded')}`
        : t('policies.openEnded')
      : `${policy.startDate ? formatDate(policy.startDate) : '—'}  →  ${formatDate(policy.endDate)}`;

  return (
    <Card style={{ gap: spacing.sm }}>
      {/* Row 1: type + name/insurer + status pill */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
        <IconTile
          icon={TYPE_ICON[policy.type]}
          bg={policy.type === 'LIFE' ? colors.navy : colors.primaryTile}
          fg={policy.type === 'LIFE' ? '#FFFFFF' : colors.navy}
        />
        <View style={{ flex: 1 }}>
          <AppText variant="headlineSm">{policy.customName ?? policy.insurerName}</AppText>
          <AppText variant="bodyMd">{policy.insurerName}</AppText>
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            backgroundColor: status.bg,
            borderRadius: radius.full,
            paddingVertical: 4,
            paddingHorizontal: spacing.sm,
          }}
        >
          <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: status.fg }} />
          <AppText variant="labelMd" color={status.fg}>
            {status.label}
          </AppText>
        </View>
      </View>

      {/* Row 2: calendar + od–do date range (replaces "koniec ochrony") */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs }}>
        <Ionicons name="calendar-outline" size={16} color={colors.outline} />
        <AppText variant="labelLg" style={{ fontSize: 15 }}>
          {dateRange}
        </AppText>
      </View>

      {/* Row 3: slim protection bar + days-left (hidden for open-ended/ended) */}
      {elapsedPct !== null && days !== null && !ended && (
        <View style={{ gap: 6 }}>
          <View style={{ height: 6, borderRadius: radius.full, backgroundColor: colors.surface, overflow: 'hidden' }}>
            <View
              style={{
                height: 6,
                width: `${elapsedPct}%`,
                borderRadius: radius.full,
                backgroundColor: endingSoon ? colors.warning : colors.success,
              }}
            />
          </View>
          <AppText variant="labelMd" color={endingSoon ? colors.warning : colors.textSecondary}>
            {t('common.daysLeft', { count: days })}
          </AppText>
        </View>
      )}

      {/* Footer: premium (compact) + details */}
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <AppText variant="labelLg" style={{ flex: 1, fontSize: 15 }}>
          {premiumValue}
        </AppText>
        <Pressable
          accessibilityRole="button"
          onPress={onDetails}
          style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.base, minHeight: 44 }}
        >
          <AppText variant="labelLg" color={colors.primary}>
            {t('policies.details')}
          </AppText>
          <Ionicons name="chevron-forward" size={16} color={colors.primary} />
        </Pressable>
      </View>
    </Card>
  );
}
